/**
 * Renders the product logo: either the brand's logoUrl as an <img>, or
 * the configured gradient square with a fallback icon. Used in header,
 * footer and login. One source of truth so brand changes propagate everywhere.
 */
import React, { useState } from 'react';
import { ApiOutlined } from '@ant-design/icons';
import { brand } from '../../theme';

interface BrandLogomarkProps {
  /** Square side length in px. Default 28 (header size). */
  size?: number;
  /** Icon shown when no logoUrl is configured. Default ApiOutlined. */
  fallbackIcon?: React.ReactNode;
  /** Border radius in px (defaults proportional to size). */
  radius?: number;
  /** Extra shadow / glow. */
  boxShadow?: string;
}

const BrandLogomark: React.FC<BrandLogomarkProps> = ({
  size = 28,
  fallbackIcon,
  radius,
  boxShadow,
}) => {
  const borderRadius = radius ?? Math.max(4, Math.round(size * 0.21));
  const iconFontSize = Math.max(10, Math.round(size * 0.45));
  // The logo is now an <img> in every default build; if it fails to load
  // (404, blocked, bad white-label URL) fall back to the gradient square
  // instead of a broken-image glyph.
  const [logoFailed, setLogoFailed] = useState(false);

  if (brand.logoUrl && !logoFailed) {
    return (
      <img
        src={brand.logoUrl}
        alt={brand.name}
        onError={() => setLogoFailed(true)}
        style={{
          width: size,
          height: size,
          borderRadius,
          objectFit: 'contain',
          flexShrink: 0,
          boxShadow,
        }}
      />
    );
  }

  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius,
        background: `linear-gradient(135deg, ${brand.gradientFrom} 0%, ${brand.gradientTo} 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        boxShadow,
        color: 'white',
      }}
    >
      {fallbackIcon ?? <ApiOutlined style={{ fontSize: iconFontSize }} />}
    </div>
  );
};

export default BrandLogomark;
